/**
 * Onboarding Wizard Page
 *
 * Account-first onboarding flow: plan selection, registration/login,
 * checkout, then language configuration.
 *
 * States: WELCOME -> TIER_SELECT -> REGISTER -> LOGIN -> CHECKOUT -> LANGUAGE_SETUP -> COMPLETE
 *
 * @package MultilingualPressZone
 * @since 1.1.0
 */

import { el, __, clear } from '../utils/dom.js';
import API from '../utils/api.js';
import { Button } from '../components/Button.js';
import { Select } from '../components/Select.js';
import { FormField } from '../components/Input.js';
import { Toggle } from '../components/Toggle.js';
import Toast from '../components/Toast.js';
import logger from '../utils/logger.js';

const STATES = {
    WELCOME: 'WELCOME',
    TIER_SELECT: 'TIER_SELECT',
    REGISTER: 'REGISTER',
    LOGIN: 'LOGIN',
    CHECKOUT: 'CHECKOUT',
    PAYPAL_WAITING: 'PAYPAL_WAITING',
    LANGUAGE_SETUP: 'LANGUAGE_SETUP',
    COMPLETE: 'COMPLETE',
};

const STEPS = [
    { id: 1, title: __('Choose Plan', 'multilingual-press-zone') },
    { id: 2, title: __('Create Account', 'multilingual-press-zone') },
    { id: 3, title: __('Activate', 'multilingual-press-zone') },
    { id: 4, title: __('Configure', 'multilingual-press-zone') },
    { id: 5, title: __('Done!', 'multilingual-press-zone') },
];

/**
 * Map state to sidebar step number
 */
function stateToStep(state) {
    switch (state) {
        case STATES.WELCOME:
        case STATES.TIER_SELECT:
            return 1;
        case STATES.REGISTER:
        case STATES.LOGIN:
            return 2;
        case STATES.CHECKOUT:
        case STATES.PAYPAL_WAITING:
            return 3;
        case STATES.LANGUAGE_SETUP:
            return 4;
        case STATES.COMPLETE:
            return 5;
        default:
            return 1;
    }
}

export default class OnboardingPage {
    constructor(container) {
        this.container = container;
        this.state = {
            currentState: STATES.WELCOME,
            plans: [],
            selectedPlan: null,
            billingCycle: 'monthly',
            email: '',
            password: '',
            isSubmitting: false,
            error: null,
            // Language setup settings
            settings: {
                default_language: 'en',
                url_structure: 'subdirectory',
                auto_translate: true,
            },
            // Checkout result
            licenseTier: null,
        };

        // PayPal polling state
        this._paypalSessionId = null;
        this._pollInterval = null;
        this._pollDeadline = null;
    }

    async render() {
        this.renderWizard();
    }

    renderWizard() {
        clear(this.container);

        const wrapper = el('div', { class: 'mpz-onboarding-wrapper' },
            this.renderSidebar(),
            this.renderContent()
        );

        this.container.appendChild(wrapper);
    }

    renderSidebar() {
        const currentStep = stateToStep(this.state.currentState);

        const logo = el('div', { class: 'mpz-logo' },
            el('span', { class: 'dashicons dashicons-translation' }),
            el('h1', {}, 'Multilingual Press Zone')
        );

        const stepList = el('ul', { class: 'mpz-steps' });

        STEPS.forEach(step => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;

            let className = 'mpz-step';
            if (isActive) className += ' active';
            if (isCompleted) className += ' completed';

            const item = el('li', { class: className },
                el('span', { class: 'step-number' }, isCompleted ? '✓' : String(step.id)),
                el('span', { class: 'step-title' }, step.title)
            );
            stepList.appendChild(item);
        });

        return el('div', { class: 'mpz-sidebar' }, logo, stepList);
    }

    renderContent() {
        const content = el('div', { class: 'mpz-content' });

        const body = el('div', { class: 'mpz-content-body' });

        switch (this.state.currentState) {
            case STATES.WELCOME:
                body.appendChild(this.renderWelcome());
                break;
            case STATES.TIER_SELECT:
                body.appendChild(this.renderTierSelect());
                break;
            case STATES.REGISTER:
                body.appendChild(this.renderRegister());
                break;
            case STATES.LOGIN:
                body.appendChild(this.renderLogin());
                break;
            case STATES.CHECKOUT:
                body.appendChild(this.renderCheckout());
                break;
            case STATES.PAYPAL_WAITING:
                body.appendChild(this.renderPaypalWaiting());
                break;
            case STATES.LANGUAGE_SETUP:
                body.appendChild(this.renderLanguageSetup());
                break;
            case STATES.COMPLETE:
                body.appendChild(this.renderComplete());
                break;
        }

        content.appendChild(body);
        return content;
    }

    // --- State: WELCOME ---

    renderWelcome() {
        const container = el('div', { class: 'step-container center-align' });

        container.appendChild(el('div', { class: 'mpz-welcome-icon' },
            el('span', { class: 'dashicons dashicons-translation' })
        ));
        container.appendChild(el('h2', { class: 'mpz-welcome-title' },
            __('Welcome to Multilingual Press Zone', 'multilingual-press-zone')
        ));
        container.appendChild(el('p', { class: 'step-description' },
            __('Transform your WordPress site into a fully multilingual experience. Let\'s get you set up in just a few steps.', 'multilingual-press-zone')
        ));

        const actions = el('div', { class: 'mpz-welcome-actions' });

        actions.appendChild(Button({
            label: __('Get Started', 'multilingual-press-zone'),
            variant: 'primary',
            size: 'lg',
            onClick: () => this.goTo(STATES.TIER_SELECT),
        }));

        actions.appendChild(Button({
            label: __('I have an account', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => this.goTo(STATES.LOGIN),
        }));

        container.appendChild(actions);
        return container;
    }

    // --- State: TIER_SELECT ---

    renderTierSelect() {
        const container = el('div', { class: 'step-container mpz-tier-step' });

        container.appendChild(el('div', { class: 'mpz-content-header' },
            el('h2', {}, __('Choose Your Plan', 'multilingual-press-zone'))
        ));

        container.appendChild(el('p', { class: 'step-description' },
            __('Select the plan that best fits your multilingual needs.', 'multilingual-press-zone')
        ));

        // Billing toggle
        const toggle = el('div', { class: 'mpz-billing-toggle' },
            el('span', {
                class: `mpz-billing-toggle__option ${this.state.billingCycle === 'monthly' ? 'mpz-billing-toggle__option--active' : ''}`,
                onclick: () => { this.state.billingCycle = 'monthly'; this.renderWizard(); },
            }, __('Monthly', 'multilingual-press-zone')),
            el('span', {
                class: `mpz-billing-toggle__option ${this.state.billingCycle === 'annual' ? 'mpz-billing-toggle__option--active' : ''}`,
                onclick: () => { this.state.billingCycle = 'annual'; this.renderWizard(); },
            }, __('Annual', 'multilingual-press-zone')),
            el('span', { class: 'mpz-billing-toggle__badge' }, __('Save 20%', 'multilingual-press-zone'))
        );
        container.appendChild(toggle);

        // Loading state
        if (this.state.plans.length === 0 && !this.state.error) {
            const loading = el('div', { class: 'mpz-tier-loading' },
                el('p', {}, __('Loading plans...', 'multilingual-press-zone'))
            );
            container.appendChild(loading);
            this.fetchPlans();
            return container;
        }

        // Error state
        if (this.state.error && this.state.plans.length === 0) {
            container.appendChild(el('div', { class: 'mpz-onboarding-error' },
                el('p', {}, this.state.error),
                Button({
                    label: __('Retry', 'multilingual-press-zone'),
                    variant: 'secondary',
                    onClick: () => { this.state.error = null; this.renderWizard(); },
                })
            ));
            return container;
        }

        // Tier cards
        const cards = el('div', { class: 'mpz-tier-cards' });

        this.state.plans.forEach(plan => {
            const isSelected = this.state.selectedPlan?.slug === plan.slug;
            const price = this.state.billingCycle === 'annual'
                ? (plan.annual_price ?? plan.monthly_price ?? 0)
                : (plan.monthly_price ?? 0);
            const priceLabel = this.state.billingCycle === 'annual'
                ? __('/year', 'multilingual-press-zone')
                : __('/month', 'multilingual-press-zone');

            const features = plan.features || [];
            const limits = plan.plugin_limits || {};

            const card = el('div', {
                class: `mpz-tier-card ${isSelected ? 'mpz-tier-card--selected' : ''} ${plan.recommended ? 'mpz-tier-card--recommended' : ''}`,
                onclick: () => {
                    this.state.selectedPlan = plan;
                    this.renderWizard();
                },
            },
                plan.recommended
                    ? el('div', { class: 'mpz-tier-card__badge' }, __('Recommended', 'multilingual-press-zone'))
                    : null,
                el('h3', { class: 'mpz-tier-card__name' }, plan.name),
                el('div', { class: 'mpz-tier-card__price' },
                    el('span', { class: 'mpz-tier-card__amount' }, `$${price}`),
                    el('span', { class: 'mpz-tier-card__period' }, priceLabel)
                ),
                limits.languages_allowed
                    ? el('p', { class: 'mpz-tier-card__languages' },
                        limits.languages_allowed === -1
                            ? __('Unlimited languages', 'multilingual-press-zone')
                            : `${limits.languages_allowed} ${__('languages', 'multilingual-press-zone')}`
                    )
                    : null,
                el('ul', { class: 'mpz-tier-card__features' },
                    ...features.map(f => el('li', {}, f))
                ),
                el('div', { class: 'mpz-tier-card__select' },
                    Button({
                        label: isSelected ? __('Selected', 'multilingual-press-zone') : __('Select', 'multilingual-press-zone'),
                        variant: isSelected ? 'primary' : 'secondary',
                        size: 'sm',
                        onClick: (e) => {
                            e.stopPropagation();
                            this.state.selectedPlan = plan;
                            this.renderWizard();
                        },
                    })
                )
            );

            cards.appendChild(card);
        });

        container.appendChild(cards);

        // Footer
        const footer = el('div', { class: 'mpz-content-footer' });
        footer.appendChild(Button({
            label: __('Back', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => this.goTo(STATES.WELCOME),
        }));
        footer.appendChild(Button({
            label: __('Continue', 'multilingual-press-zone'),
            variant: 'primary',
            disabled: !this.state.selectedPlan,
            onClick: () => this.goTo(STATES.REGISTER),
        }));
        container.appendChild(footer);

        return container;
    }

    async fetchPlans() {
        try {
            const response = await API.get('/onboarding/plans');
            this.state.plans = response.data?.plans || response.plans || response.data || [];
            if (!Array.isArray(this.state.plans)) {
                this.state.plans = [];
            }
            this.state.error = null;
        } catch (err) {
            logger.error('Failed to fetch plans', err);
            this.state.error = err.message || __('Failed to load plans.', 'multilingual-press-zone');
        }
        this.renderWizard();
    }

    // --- State: REGISTER ---

    renderRegister() {
        const container = el('div', { class: 'step-container' });

        container.appendChild(el('div', { class: 'mpz-content-header' },
            el('h2', {}, __('Create Your Account', 'multilingual-press-zone'))
        ));

        container.appendChild(el('p', { class: 'step-description' },
            __('Create a Press.Zone account to manage your license and access the translation platform.', 'multilingual-press-zone')
        ));

        if (this.state.error) {
            container.appendChild(el('div', { class: 'mpz-onboarding-error' },
                el('p', {}, this.state.error)
            ));
        }

        const form = el('div', { class: 'mpz-onboarding-form' });

        const emailState = { email: this.state.email };
        form.appendChild(FormField(
            'email',
            __('Email Address', 'multilingual-press-zone'),
            '',
            'email',
            emailState,
            (key, value) => { this.state.email = value; }
        ));

        const passwordState = { password: this.state.password };
        form.appendChild(FormField(
            'password',
            __('Password', 'multilingual-press-zone'),
            __('At least 6 characters', 'multilingual-press-zone'),
            'password',
            passwordState,
            (key, value) => { this.state.password = value; }
        ));

        container.appendChild(form);

        // Login link
        container.appendChild(el('p', { class: 'mpz-onboarding-link' },
            __('Already have an account?', 'multilingual-press-zone'),
            ' ',
            el('a', {
                href: '#',
                onclick: (e) => { e.preventDefault(); this.goTo(STATES.LOGIN); },
            }, __('Log in', 'multilingual-press-zone'))
        ));

        // Footer
        const footer = el('div', { class: 'mpz-content-footer' });
        footer.appendChild(Button({
            label: __('Back', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => this.goTo(STATES.TIER_SELECT),
        }));
        footer.appendChild(Button({
            label: this.state.isSubmitting ? __('Creating Account...', 'multilingual-press-zone') : __('Create Account', 'multilingual-press-zone'),
            variant: 'primary',
            disabled: this.state.isSubmitting,
            onClick: () => this.handleRegister(),
        }));
        container.appendChild(footer);

        return container;
    }

    async handleRegister() {
        if (!this.state.email || !this.state.password) {
            this.state.error = __('Please fill in all fields.', 'multilingual-press-zone');
            this.renderWizard();
            return;
        }

        if (this.state.password.length < 6) {
            this.state.error = __('Password must be at least 6 characters.', 'multilingual-press-zone');
            this.renderWizard();
            return;
        }

        this.state.isSubmitting = true;
        this.state.error = null;
        this.renderWizard();

        try {
            await API.post('/onboarding/register', {
                email: this.state.email,
                password: this.state.password,
            });
            this.state.isSubmitting = false;
            this.goTo(STATES.CHECKOUT);
        } catch (err) {
            this.state.isSubmitting = false;
            this.state.error = err.message || __('Registration failed. Please try again.', 'multilingual-press-zone');
            this.renderWizard();
        }
    }

    // --- State: LOGIN ---

    renderLogin() {
        const container = el('div', { class: 'step-container' });

        container.appendChild(el('div', { class: 'mpz-content-header' },
            el('h2', {}, __('Log In to Your Account', 'multilingual-press-zone'))
        ));

        container.appendChild(el('p', { class: 'step-description' },
            __('Enter your Press.Zone credentials to continue.', 'multilingual-press-zone')
        ));

        if (this.state.error) {
            container.appendChild(el('div', { class: 'mpz-onboarding-error' },
                el('p', {}, this.state.error)
            ));
        }

        const form = el('div', { class: 'mpz-onboarding-form' });

        const emailState = { email: this.state.email };
        form.appendChild(FormField(
            'email',
            __('Email Address', 'multilingual-press-zone'),
            '',
            'email',
            emailState,
            (key, value) => { this.state.email = value; }
        ));

        const passwordState = { password: this.state.password };
        form.appendChild(FormField(
            'password',
            __('Password', 'multilingual-press-zone'),
            '',
            'password',
            passwordState,
            (key, value) => { this.state.password = value; }
        ));

        container.appendChild(form);

        // Register link
        container.appendChild(el('p', { class: 'mpz-onboarding-link' },
            __('Don\'t have an account?', 'multilingual-press-zone'),
            ' ',
            el('a', {
                href: '#',
                onclick: (e) => { e.preventDefault(); this.goTo(STATES.REGISTER); },
            }, __('Create one', 'multilingual-press-zone'))
        ));

        // Footer
        const footer = el('div', { class: 'mpz-content-footer' });
        footer.appendChild(Button({
            label: __('Back', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => this.goTo(STATES.WELCOME),
        }));
        footer.appendChild(Button({
            label: this.state.isSubmitting ? __('Logging in...', 'multilingual-press-zone') : __('Log In', 'multilingual-press-zone'),
            variant: 'primary',
            disabled: this.state.isSubmitting,
            onClick: () => this.handleLogin(),
        }));
        container.appendChild(footer);

        return container;
    }

    async handleLogin() {
        if (!this.state.email || !this.state.password) {
            this.state.error = __('Please fill in all fields.', 'multilingual-press-zone');
            this.renderWizard();
            return;
        }

        this.state.isSubmitting = true;
        this.state.error = null;
        this.renderWizard();

        try {
            const response = await API.post('/onboarding/login', {
                email: this.state.email,
                password: this.state.password,
            });
            this.state.isSubmitting = false;

            if (response.has_subscription) {
                // User already has a subscription, go straight to checkout
                this.goTo(STATES.CHECKOUT);
            } else {
                // No subscription yet, pick a tier first
                if (!this.state.selectedPlan) {
                    this.goTo(STATES.TIER_SELECT);
                } else {
                    this.goTo(STATES.CHECKOUT);
                }
            }
        } catch (err) {
            this.state.isSubmitting = false;
            this.state.error = err.message || __('Login failed. Please check your credentials.', 'multilingual-press-zone');
            this.renderWizard();
        }
    }

    // --- State: CHECKOUT ---

    renderCheckout() {
        const container = el('div', { class: 'step-container' });
        const plan = this.state.selectedPlan;

        container.appendChild(el('div', { class: 'mpz-content-header' },
            el('h2', {}, __('Complete Your Purchase', 'multilingual-press-zone'))
        ));

        if (this.state.error) {
            container.appendChild(el('div', { class: 'mpz-onboarding-error' },
                el('p', {}, this.state.error)
            ));
        }

        if (!plan) {
            container.appendChild(el('p', { class: 'step-description' },
                __('No plan selected. Please go back and choose a plan.', 'multilingual-press-zone')
            ));
            const footer = el('div', { class: 'mpz-content-footer' });
            footer.appendChild(Button({
                label: __('Choose Plan', 'multilingual-press-zone'),
                variant: 'primary',
                onClick: () => this.goTo(STATES.TIER_SELECT),
            }));
            container.appendChild(footer);
            return container;
        }

        const price = this.state.billingCycle === 'annual'
            ? (plan.annual_price ?? plan.monthly_price ?? 0)
            : (plan.monthly_price ?? 0);
        const cycleLabel = this.state.billingCycle === 'annual'
            ? __('per year', 'multilingual-press-zone')
            : __('per month', 'multilingual-press-zone');

        // Summary card
        const summary = el('div', { class: 'mpz-checkout-summary' },
            el('div', { class: 'mpz-checkout-summary__plan' },
                el('h3', {}, plan.name),
                el('p', { class: 'mpz-checkout-summary__cycle' },
                    this.state.billingCycle === 'annual'
                        ? __('Annual billing', 'multilingual-press-zone')
                        : __('Monthly billing', 'multilingual-press-zone')
                )
            ),
            el('div', { class: 'mpz-checkout-summary__price' },
                el('span', { class: 'mpz-checkout-summary__amount' }, `$${price}`),
                el('span', { class: 'mpz-checkout-summary__period' }, cycleLabel)
            ),
            el('div', { class: 'mpz-checkout-summary__details' },
                el('p', {},
                    __('Account:', 'multilingual-press-zone'),
                    ' ',
                    el('strong', {}, this.state.email)
                )
            )
        );
        container.appendChild(summary);

        // Features included
        if (plan.features && plan.features.length > 0) {
            const featuresList = el('div', { class: 'mpz-checkout-features' },
                el('h4', {}, __('Included:', 'multilingual-press-zone')),
                el('ul', {},
                    ...plan.features.map(f => el('li', {}, f))
                )
            );
            container.appendChild(featuresList);
        }

        // Footer
        const footer = el('div', { class: 'mpz-content-footer' });
        footer.appendChild(Button({
            label: __('Back', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => this.goTo(STATES.TIER_SELECT),
            disabled: this.state.isSubmitting,
        }));
        footer.appendChild(Button({
            label: this.state.isSubmitting ? __('Processing...', 'multilingual-press-zone') : __('Complete Purchase', 'multilingual-press-zone'),
            variant: 'primary',
            disabled: this.state.isSubmitting,
            onClick: () => this.handleCheckout(),
        }));
        footer.appendChild(Button({
            label: this.state.isSubmitting ? __('Processing...', 'multilingual-press-zone') : __('Continue with PayPal', 'multilingual-press-zone'),
            variant: 'secondary',
            disabled: this.state.isSubmitting,
            onClick: () => this.handlePaypalCheckout(),
        }));
        container.appendChild(footer);

        return container;
    }

    async handleCheckout() {
        const plan = this.state.selectedPlan;
        if (!plan) return;

        this.state.isSubmitting = true;
        this.state.error = null;
        this.renderWizard();

        try {
            const response = await API.post('/onboarding/checkout', {
                tier_slug: plan.slug,
                billing_cycle: this.state.billingCycle,
            });

            this.state.isSubmitting = false;
            this.state.licenseTier = response.tier || plan.name;
            this.goTo(STATES.LANGUAGE_SETUP);
        } catch (err) {
            this.state.isSubmitting = false;
            this.state.error = err.message || __('Checkout failed. Please try again.', 'multilingual-press-zone');
            this.renderWizard();
        }
    }

    // --- PayPal Checkout ---

    async handlePaypalCheckout() {
        // Open a blank tab synchronously while still in the click handler so popup
        // blockers treat it as a user-initiated gesture. We navigate it once the
        // API responds with the approval URL.
        const popup = window.open('about:blank', '_blank');

        this.state.isSubmitting = true;
        this.state.error = null;
        this.renderWizard();

        try {
            const response = await API.post('/onboarding/paypal-checkout', {
                tier_slug: this.state.selectedPlan?.slug || 'free',
                billing_cycle: this.state.billingCycle,
            });

            this.state.isSubmitting = false;

            if (!response.approval_url || !response.checkout_session_id) {
                if (popup) popup.close();
                this.state.error = __('Invalid response from payment service. Please try again.', 'multilingual-press-zone');
                this.renderWizard();
                return;
            }

            // Navigate the pre-opened tab to the PayPal approval URL.
            if (popup) {
                popup.location.href = response.approval_url;
            } else {
                // Popup was blocked — fall back to same-tab redirect as last resort.
                window.location.href = response.approval_url;
                return;
            }

            // Transition to waiting state and start polling.
            this._paypalSessionId = response.checkout_session_id;
            this.goTo(STATES.PAYPAL_WAITING);
            this._startPaypalPolling();
        } catch (err) {
            if (popup) popup.close();
            this.state.isSubmitting = false;
            this.state.error = err.message || __('Failed to start PayPal checkout. Please try again.', 'multilingual-press-zone');
            this.renderWizard();
        }
    }

    _startPaypalPolling() {
        this._stopPaypalPolling();
        this._pollDeadline = Date.now() + 5 * 60 * 1000; // 5 minutes

        this._pollInterval = setInterval(() => {
            this._pollPaypalSession();
        }, 3000);
    }

    _stopPaypalPolling() {
        if (this._pollInterval !== null) {
            clearInterval(this._pollInterval);
            this._pollInterval = null;
        }
    }

    async _pollPaypalSession() {
        if (Date.now() > this._pollDeadline) {
            this._stopPaypalPolling();
            this.state.error = __('PayPal confirmation timed out. Please check your license page or try again.', 'multilingual-press-zone');
            this.goTo(STATES.CHECKOUT);
            return;
        }

        try {
            const response = await API.get('/onboarding/paypal-session/' + this._paypalSessionId);
            const status = response.status;

            if (status === 'retrieved') {
                this._stopPaypalPolling();
                this.state.licenseTier = response.tier || this.state.selectedPlan?.name || '';
                this.goTo(STATES.LANGUAGE_SETUP);
            } else if (status === 'already_retrieved') {
                this._stopPaypalPolling();
                this.state.licenseTier = this.state.selectedPlan?.name || '';
                this.goTo(STATES.LANGUAGE_SETUP);
            } else if (status === 'pending') {
                // Keep polling — nothing to do here.
            }
            // status 503/NOT_READY: keep polling (webhook not arrived yet).
        } catch (err) {
            // On network error or 404/410, stop polling and surface an error.
            this._stopPaypalPolling();
            const code = err.code || err.status || '';
            if (code === 410) {
                this.state.error = __('PayPal session expired or payment failed. Please try again.', 'multilingual-press-zone');
            } else if (code === 404) {
                this.state.error = __('PayPal session not found. Please try again.', 'multilingual-press-zone');
            } else {
                this.state.error = err.message || __('Failed to confirm PayPal payment. Please check your license page.', 'multilingual-press-zone');
            }
            this.goTo(STATES.CHECKOUT);
        }
    }

    renderPaypalWaiting() {
        const container = el('div', { class: 'step-container center-align' });

        container.appendChild(el('div', { class: 'mpz-paypal-waiting' },
            el('span', { class: 'spinner is-active' }),
            el('p', { class: 'step-description' },
                __('Waiting for PayPal confirmation...', 'multilingual-press-zone')
            ),
            el('p', { class: 'step-description' },
                __('Complete the payment in the PayPal tab that opened. This page will update automatically.', 'multilingual-press-zone')
            )
        ));

        const footer = el('div', { class: 'mpz-content-footer' });
        footer.appendChild(Button({
            label: __('Cancel', 'multilingual-press-zone'),
            variant: 'ghost',
            onClick: () => {
                this._stopPaypalPolling();
                this.goTo(STATES.CHECKOUT);
            },
        }));
        container.appendChild(footer);

        return container;
    }

    // --- State: LANGUAGE_SETUP ---

    renderLanguageSetup() {
        const container = el('div', { class: 'step-container' });

        container.appendChild(el('div', { class: 'mpz-content-header' },
            el('h2', {}, __('Configure Your Languages', 'multilingual-press-zone'))
        ));

        container.appendChild(el('p', { class: 'step-description' },
            __('Set up your default language and URL structure. You can change these later in Settings.', 'multilingual-press-zone')
        ));

        // Language selection
        container.appendChild(el('h3', { class: 'mpz-setup-section-title' },
            __('Default Language', 'multilingual-press-zone')
        ));
        container.appendChild(el('p', { class: 'step-description' },
            __('Choose the default language for your website. This is usually the language your content is currently written in.', 'multilingual-press-zone')
        ));

        container.appendChild(Select(
            'default_language',
            __('Default Language', 'multilingual-press-zone'),
            '',
            [
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'French' },
                { value: 'de', label: 'German' },
                { value: 'it', label: 'Italian' },
                { value: 'pt', label: 'Portuguese' },
                { value: 'nl', label: 'Dutch' },
                { value: 'ru', label: 'Russian' },
                { value: 'ja', label: 'Japanese' },
                { value: 'zh', label: 'Chinese' },
                { value: 'ko', label: 'Korean' },
                { value: 'ar', label: 'Arabic' },
                { value: 'he', label: 'Hebrew' },
                { value: 'hi', label: 'Hindi' },
                { value: 'tr', label: 'Turkish' },
                { value: 'pl', label: 'Polish' },
                { value: 'sv', label: 'Swedish' },
                { value: 'da', label: 'Danish' },
                { value: 'fi', label: 'Finnish' },
                { value: 'no', label: 'Norwegian' },
            ],
            this.state.settings,
            (key, value) => { this.state.settings[key] = value; }
        ));

        // URL Structure
        container.appendChild(el('h3', { class: 'mpz-setup-section-title' },
            __('URL Structure', 'multilingual-press-zone')
        ));
        container.appendChild(el('p', { class: 'step-description' },
            __('How should translated URLs look? This affects your SEO structure.', 'multilingual-press-zone')
        ));

        const urlOptions = [
            {
                value: 'subdirectory',
                label: __('Sub-directories (Recommended)', 'multilingual-press-zone'),
                desc: 'example.com/fr/sample-page',
            },
            {
                value: 'subdomain',
                label: __('Sub-domains', 'multilingual-press-zone'),
                desc: 'fr.example.com/sample-page',
            },
            {
                value: 'parameter',
                label: __('URL Parameters', 'multilingual-press-zone'),
                desc: 'example.com/sample-page?lang=fr',
            },
        ];

        urlOptions.forEach(opt => {
            const isSelected = this.state.settings.url_structure === opt.value;
            const card = el('div', {
                class: `option-card ${isSelected ? 'selected' : ''}`,
                onclick: () => {
                    this.state.settings.url_structure = opt.value;
                    this.renderWizard();
                },
            },
                el('div', { class: 'option-radio' }, isSelected ? '●' : '○'),
                el('div', { class: 'option-content' },
                    el('strong', {}, opt.label),
                    el('span', {}, opt.desc)
                )
            );
            container.appendChild(card);
        });

        // Auto-translate toggle
        container.appendChild(el('hr', { class: 'spacer' }));
        container.appendChild(Toggle(
            'auto_translate',
            __('Enable Auto-Translation', 'multilingual-press-zone'),
            __('Automatically translate content when you publish new posts.', 'multilingual-press-zone'),
            this.state.settings,
            (key, value) => { this.state.settings[key] = value; }
        ));

        // Footer
        const footer = el('div', { class: 'mpz-content-footer' });
        footer.appendChild(Button({
            label: this.state.isSubmitting ? __('Saving...', 'multilingual-press-zone') : __('Continue', 'multilingual-press-zone'),
            variant: 'primary',
            disabled: this.state.isSubmitting,
            onClick: () => this.handleSaveSettings(),
        }));
        container.appendChild(footer);

        return container;
    }

    async handleSaveSettings() {
        this.state.isSubmitting = true;
        this.renderWizard();

        try {
            await API.post('/settings', this.state.settings);
            this.state.isSubmitting = false;
            this.goTo(STATES.COMPLETE);
        } catch (err) {
            this.state.isSubmitting = false;
            logger.error('Failed to save settings', err);
            Toast.error(err.message || __('Failed to save settings.', 'multilingual-press-zone'));
            this.renderWizard();
        }
    }

    // --- State: COMPLETE ---

    renderComplete() {
        const container = el('div', { class: 'step-container center-align mpz-complete-screen' });

        container.appendChild(el('div', { class: 'success-icon' }, '🎉'));
        container.appendChild(el('h2', {}, __('You\'re All Set!', 'multilingual-press-zone')));
        container.appendChild(el('p', { class: 'step-description' },
            __('Your multilingual site is configured and ready to go. Start translating your content and reach a global audience.', 'multilingual-press-zone')
        ));

        if (this.state.licenseTier) {
            container.appendChild(el('p', { class: 'mpz-complete-tier' },
                __('Active Plan:', 'multilingual-press-zone'),
                ' ',
                el('strong', {}, this.state.licenseTier)
            ));
        }

        container.appendChild(Button({
            label: this.state.isSubmitting ? __('Finishing...', 'multilingual-press-zone') : __('Go to Dashboard', 'multilingual-press-zone'),
            variant: 'primary',
            size: 'lg',
            disabled: this.state.isSubmitting,
            onClick: () => this.handleComplete(),
        }));

        return container;
    }

    async handleComplete() {
        this.state.isSubmitting = true;
        this.renderWizard();

        try {
            await API.post('/onboarding/complete');
            window.location.reload();
        } catch (err) {
            this.state.isSubmitting = false;
            logger.error('Failed to complete onboarding', err);
            Toast.error(err.message || __('Failed to complete setup.', 'multilingual-press-zone'));
            this.renderWizard();
        }
    }

    // --- Navigation helper ---

    goTo(state) {
        this.state.currentState = state;
        this.state.error = null;
        this.renderWizard();
    }

    destroy() {
        this._stopPaypalPolling();
    }
}
